import db from "../database";
import { tracePreparedBill } from "./preparedBillRecovery";
import { findOpenPreparedBillForCart } from "./cartIdentityGuard";

/**
 * TIC-11390 round 2: a table's open Prepare Bill, judged from the saved db.orders row
 * instead of the on-screen cart's preparedBillOrigin flag.
 *
 * The Cancel / Prepare Bill guards (TSK-3120, TSK-3303) only looked at the cart flag. When
 * the cart lost it (table reopen, reload), Cancel KOT was enabled again and a second Prepare
 * Bill or payment created a new order, leaving the first one completed and un-invoiced in CW:
 *  - HMRS/10843 (F1, 4-Oct): line D0D3C76D cancelled after Prepare Bill, table re-billed as
 *    HMRS/10844 carrying the same four fbOrderLineIds.
 *  - HMRS/10821 (G8, 3-Oct): whole prepared bill cancelled from a cart with the bill's
 *    sOrderID but no flag; the cancel took a fresh number (HMRS/10934) and never reached CW.
 *
 * A bill belongs to the table's CURRENT session when it shares at least one fbOrderLineId
 * with it. That keeps an old bill left open on the same table (HMRS/10843 vs the next guests'
 * HMRS/10845 — no shared line) from locking the table for later guests.
 */

export const isOpenPreparedBillRow = (o) =>
  !!o && o.preparedBillOrigin === "Y" && !(Number(o.paid) > 0) && Number(o.total) > 0 && !!o.documentno && o.isSplit !== "Y" && !o.splitTable;

/** fbOrderLineIds of every line, cancelled ones included — a cancelled line still identifies the session. */
export const sessionLineIds = (...itemLists) => {
  const ids = new Set();
  for (const items of itemLists) {
    for (const item of items || []) {
      if (item?.fbOrderLineId) ids.add(item.fbOrderLineId);
    }
  }
  return ids;
};

/**
 * Matched on shared fbOrderLineId only, not on the bill row's tableId: that row keeps the
 * table Prepare Bill was clicked on, so after Transfer Bill the session is on another table
 * (HMRS/10875 F1→H1, 10892 F3→G1; preprod HMRS/2722 F5→F7 was paid as a second order).
 * fbOrderLineId is unique per table session, so this never reaches another session's bill.
 * Callers pass the session's CURRENT table only to mark it as dine-in.
 */
export const findOpenPreparedBillsInSession = ({ orders, lineIds, excludeSOrderID } = {}) => {
  if (!lineIds || lineIds.size === 0) return [];
  return (orders || []).filter(
    (o) => isOpenPreparedBillRow(o) && o.sOrderID !== excludeSOrderID && (o.items || []).some((i) => i?.fbOrderLineId && lineIds.has(i.fbOrderLineId))
  );
};

/** Open prepared bills of this table session, read from db.orders. Never throws. */
export const getOpenPreparedBillsForSession = async ({ tableId, items, excludeSOrderID } = {}) => {
  try {
    const lineIds = sessionLineIds(items);
    // No table = not a dine-in session (take-away, retail): nothing to read.
    if (!tableId || lineIds.size === 0) return [];
    // toArray + JS filter: .filter() closures are lost over the CW Browser SQLite bridge
    const orders = await db.orders.toArray();
    return findOpenPreparedBillsInSession({ orders, tableId, lineIds, excludeSOrderID });
  } catch {
    return [];
  }
};

/** Every item a table session row carries: server lines, its cart and its server snapshot. */
export const fbOrderSessionItems = (fbOrder) => [...(fbOrder?.lines || []), ...(fbOrder?.cart?.items || []), ...(fbOrder?.wsData?.items || [])];

/**
 * TIC-11390 round 4: split table sessions that are about to be force-closed (another session
 * on the table completed, or the server reports a different / no active order for it) into
 * those that may close and those that hold an open prepared bill and must stay "IP".
 * HMRES01 9-11 Oct: after Prepare Bill the waiter app opened a SECOND session on the table
 * (one Mineral Water); opening the table loaded only that session and closed the bill's one,
 * so the cashier was charged Rs 9.99 and the bill stayed completed and un-invoiced in CW
 * (G4 HMRS/11127 -> HMRS/11147, G6 HMRS/11203 -> HMRS/11210). Kept sessions are merged into
 * the cart, so the payment covers the bill and supersedes it. Never throws: if db.orders
 * cannot be read every session is closable, i.e. the behaviour before this check.
 */
export const partitionSessionsByOpenPreparedBill = async (sessions = []) => {
  if (!sessions.length) return { closable: [], keep: [] };
  let orders;
  try {
    orders = await db.orders.toArray();
  } catch {
    return { closable: [...sessions], keep: [] };
  }
  const closable = [];
  const keep = [];
  for (const session of sessions) {
    const bills = findOpenPreparedBillsInSession({ orders, lineIds: sessionLineIds(fbOrderSessionItems(session)) });
    if (bills.length > 0) keep.push({ session, bills });
    else closable.push(session);
  }
  return { closable, keep };
};

/**
 * Force-close (CL) the stale IP sessions of a table after another of its orders completed,
 * except those holding an open prepared bill (see partitionSessionsByOpenPreparedBill).
 * Returns the kept sessions. Used by the websocket ORDER_UPSERT handler.
 */
export const closeStaleSessionsKeepingPreparedBills = async (staleSessions, tableId, completedFbOrderId) => {
  const { closable, keep } = await partitionSessionsByOpenPreparedBill(staleSessions);
  for (const stale of closable) {
    await db.fbOrderData.update(stale.id, { fbOrderStatus: "CL" });
  }
  if (keep.length > 0) {
    tracePreparedBill("stale-session-kept-prepared-bill", {
      flow: "order-upsert",
      tableId,
      completedFbOrderId,
      kept: keep.map((k) => ({ fbOrderId: k.session.fbOrderId, bills: k.bills.map((b) => b.documentno) })),
    });
  }
  return keep.map((k) => k.session);
};

/**
 * The bill's row as a cancelled Prepare Bill, in the shape clearCart1 (Restaurant/index.js)
 * writes for "Cancel All": same sOrderID and documentno, totals and lines zeroed, isSynced 0.
 * syncOrders then classifies it as isPrepareBillCancelled and confirms it through
 * confirmPOSLayawayOrder1, or — when the bill's own create never reached CW (isSynced !== 1) —
 * as an ordinary cancelled order through posOrderProcessor1.
 */
const zeroItem = (item) => {
  const zeroed = {
    ...item,
    salePrice: 0,
    sunitprice: 0,
    netStd: 0,
    nettotal: 0,
    unitTax: 0,
    taxAmount: 0,
    weight: 0,
    unitPrice: 0,
    isReturn: false,
    netList: 0,
    discount: 0,
    linetax: 0,
    linenet: 0,
    linegross: 0,
    linestd: 0,
    netunit: 0,
    listPrice: 0,
    grossunit: 0,
    grossstd: 0,
    grosslist: 0,
  };
  if (Array.isArray(item?.children) && item.children.length > 0) zeroed.children = item.children.map(zeroItem);
  return zeroed;
};

export const buildPreparedBillCancelRecord = (bill, { supersededBy, reason } = {}) => {
  const confirmed = bill.isSynced === 1 || bill.isSynced === "1";
  return {
    ...bill,
    items: (bill.items || []).map(zeroItem),
    tax: 0,
    total: 0,
    totalQty: 0,
    discount: 0,
    couponInput: [],
    docType: "Invoice",
    isReturn: false,
    paid: 0,
    payments: [],
    preparedBillOrigin: "Y",
    preparedBillConfirmed: confirmed ? "Y" : "N",
    layAway: confirmed ? "Y" : "N",
    isPrepareBillCreate: "N",
    isSynced: 0,
    syncAttempts: 0,
    supersededBy: supersededBy || null,
    cancelReason: reason || "superseded",
  };
};

/**
 * Cancel every open prepared bill of this table session except `keepSOrderID` — the order
 * the cashier is actually settling. Used when a payment goes ahead under a different order
 * (lines changed after Prepare Bill), so the earlier bill is cancelled in CW instead of being
 * left completed and un-invoiced. Returns the cancelled bills. Never throws.
 */
export const cancelSupersededPreparedBills = async ({ tableId, items, keepSOrderID, flow }) => {
  const bills = await getOpenPreparedBillsForSession({ tableId, items, excludeSOrderID: keepSOrderID });
  return cancelPreparedBills({ bills, keepSOrderID, tableId, flow });
};

/**
 * Same, for bills named by sOrderID — used by processOrderApproved right AFTER the paid order
 * is saved. It must not run before: with disableOfflineBilling "Y", syncOrders sends pending
 * db.orders rows INSTEAD of the live cart on that click, so a cancel record written at the
 * start of payment would be sent in place of the payment.
 */
export const cancelPreparedBillsByIds = async ({ sOrderIDs, keepSOrderID, flow }) => {
  const bills = [];
  for (const id of sOrderIDs || []) {
    if (!id || id === keepSOrderID) continue;
    try {
      const row = await db.orders.get(id);
      if (row) bills.push(row);
    } catch {
      /* skipped */
    }
  }
  return cancelPreparedBills({ bills, keepSOrderID, tableId: bills[0]?.tableId, flow });
};

const cancelPreparedBills = async ({ bills, keepSOrderID, tableId, flow }) => {
  const cancelled = [];
  for (const bill of bills) {
    try {
      // Re-read right before writing: the bill may have been paid or cancelled meanwhile.
      const current = await db.orders.get(bill.sOrderID);
      if (!isOpenPreparedBillRow(current)) continue;
      await db.orders.put(buildPreparedBillCancelRecord(current, { supersededBy: keepSOrderID, reason: flow }));
      cancelled.push(current);
      tracePreparedBill("prepared-bill-superseded-cancelled", {
        flow,
        sOrderID: current.sOrderID,
        documentno: current.documentno,
        total: current.total,
        keepSOrderID,
        tableId,
        confirmedOnServer: current.isSynced === 1,
      });
    } catch (err) {
      tracePreparedBill("prepared-bill-superseded-cancel-failed", { flow, sOrderID: bill.sOrderID, error: String(err?.message || err).slice(0, 200) });
    }
  }
  return cancelled;
};

/**
 * What a cart WITHOUT preparedBillOrigin should do about its session's open prepared bills
 * (`bills` = getOpenPreparedBillsForSession, cart's own sOrderID included):
 *  - "none"      no open prepared bill in this session.
 *  - "adopt"     the cart IS that bill (same sOrderID) and still holds all of its fired lines,
 *                uncancelled at the same qty (BUG-2425's rule): restore the identity — reprint /
 *                confirm it, no new order. A matching documentno alone is not enough: the CW
 *                confirm keeps the lines Prepare Bill saved, so it would invoice a line the
 *                cashier has since cancelled.
 *  - "supersede" the bill's lines changed after Prepare Bill: a new order is needed, and every
 *                open bill of the session must be cancelled. `regenerateId` when the cart still
 *                holds a bill's sOrderID, so the new order and the cancel never share a key.
 */
export const resolveSessionPreparedBill = (cart, bills = []) => {
  if (!cart || cart.preparedBillOrigin === "Y" || cart.isSplit === "Y" || bills.length === 0) return { action: "none" };
  const same = bills.find((b) => b.sOrderID === cart.sOrderID);
  if (same) {
    const linesMatch = !!findOpenPreparedBillForCart({ cart: { ...cart, sOrderID: undefined, preparedBillOrigin: undefined }, orders: [same] });
    if (linesMatch) {
      return { action: "adopt", bill: same, others: bills.filter((b) => b !== same) };
    }
    return { action: "supersede", bills, regenerateId: true };
  }
  return { action: "supersede", bills, regenerateId: false };
};

export const PREPARED_BILL_LOCKED_MESSAGE = (documentno) =>
  `Bill ${documentno} is already prepared for this table. Items cannot be cancelled and a new bill cannot be prepared — use Pay Bill to settle the table.`;

/**
 * BUG-2425 (round 3): open prepared bills of the session currently on `cwrFbTableId`, from
 * the table's in-progress fbOrderData row (lines, cart and server snapshot) plus the live
 * cart when it is on that table. Used by the table-level actions that end or combine a
 * session without paying or cancelling its bill. Never throws.
 */
export const getOpenPreparedBillsForTable = async (cwrFbTableId, liveCart) => {
  try {
    if (!cwrFbTableId) return [];
    const tableOrders = await db.fbOrderData.where("cwrFbTableId").equals(cwrFbTableId).toArray();
    const ip = tableOrders.find((o) => o.fbOrderStatus === "IP");
    const items = [...(ip?.lines || []), ...(ip?.cart?.items || []), ...(ip?.wsData?.items || []), ...(liveCart?.tableId === cwrFbTableId ? liveCart.items || [] : [])];
    return getOpenPreparedBillsForSession({ tableId: cwrFbTableId, items });
  } catch {
    return [];
  }
};

/**
 * BUG-2575: the table's own status says a bill was prepared ("BRQ", Bill Requested). The bill row
 * lives only in the db.orders of the device that prepared it, so every other device (and the same
 * device after a Full Sync) finds no open bill in db.orders; the table status is what the server
 * sends to all of them. Never throws.
 */
export const isTableBillRequested = async (cwrFbTableId) => {
  try {
    if (!cwrFbTableId) return false;
    const row = await db.tableData.get(cwrFbTableId);
    return row?.statusType === "BRQ";
  } catch {
    return false;
  }
};

export const BILL_REQUESTED_LOCKED_MESSAGE = "A bill is already prepared for this table. Items cannot be cancelled — use Pay Bill to settle the table.";

export const BILL_REQUESTED_TABLE_ACTION_MESSAGE = (tableName, action) =>
  `A bill is already prepared for table ${tableName}, so it cannot be ${action}. Settle it with Pay Bill first.`;

/**
 * BUG-2425 (round 3): prepared bills of this till session that were never paid or cancelled.
 * A bill whose table session was closed or moved on (HMRS/10854, 10901, 10636) leaves its
 * table free, so the "tables still have ongoing orders" check at Close Till never sees it and
 * it stays completed and un-invoiced in CW. Never throws.
 */
export const getOpenPreparedBillsForTillSession = async (tillSessionId) => {
  try {
    if (!tillSessionId) return [];
    const orders = await db.orders.where("tillSessionId").equals(tillSessionId).toArray();
    return orders.filter(isOpenPreparedBillRow);
  } catch {
    return [];
  }
};

/**
 * Closing ("Cancel" table) or merging a table whose session has an open prepared bill left
 * that bill completed and un-invoiced in CW: the session was closed with no payment and no
 * cancel sent (HMRS/10854, 10636), or two billed sessions were merged into one cart that
 * matched neither bill (HMRS/10803 + 10812). Refused; the bill is paid or cancelled first.
 */
export const PREPARED_BILL_TABLE_ACTION_MESSAGE = (documentno, action) =>
  `Bill ${documentno} is already prepared for this table, so it cannot be ${action}. Settle it with Pay Bill first, or cancel it from the order screen where cancellation is allowed.`;
